//@third-party
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './Subject.scss';
import { FlowcellControl } from '@models/upload';
import {
  generateMRDStatusText,
  ISubjectDispatchProps,
  ISubjectStateProps,
  MrdStatus,
  TestTypes,
} from './constants';
import './Subject.scss';

const IdAndCalibHighlightCases = [MrdStatus.MrdEnabled, MrdStatus.IdPending];
const statusHighlightList: {
  [index in Exclude<TestTypes, null>]: MrdStatus[];
} = {
  ID: IdAndCalibHighlightCases,
  MRD: [MrdStatus.NoClonesFound, MrdStatus.IdPending, MrdStatus.NA],
  CALIB: IdAndCalibHighlightCases,
};

const statusTextMap = {
  [MrdStatus.MrdEnabled]: 'upload_mrdStatus_tracking',
  [MrdStatus.IdPending]: 'upload_mrdStatus_processing',
  [MrdStatus.NA]: 'upload_mrdStatus_missing',
  [MrdStatus.NoClonesFound]: 'upload_mrdStatus_noClones',
};

const Subject: React.FC<ISubjectStateProps & ISubjectDispatchProps> = ({
  name,
  wells,
  select,
  selected,
  testType,
  properties: statuses,
  controlType,
  displayColor,
  externalSubjectId,
  newExperiment: isNewExperiment,
}) => {
  // state
  const [statusText, setStatusText] = useState<string>('');
  const [sampleType, setSampleType] = useState<string>('');
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false);
  const [isControl, setIsControl] = useState<boolean>(true);
  // translations
  const { t } = useTranslation();

  useEffect(() => {
    if (controlType === undefined || controlType === FlowcellControl.none) {
      setIsControl(false);
    }
  }, [testType]);

  // check conditionals to change highlighted styling
  useEffect(() => {
    let sampleType;
    sampleType = generateSampleType(controlType, testType);
    setSampleType(sampleType);
    if (!isControl) {
      setStatusState();
    }
  }, [isControl]);

  const setStatusState = () => {
    if (statuses) {
      const status: string = generateMRDStatusText(statuses) as MrdStatus;
      if (testType) {
        const isHighlighted: boolean = Object.values(
          statusHighlightList[testType]
        ).includes(status as MrdStatus);
        setIsHighlighted(isHighlighted);
      }
      const statusText = statusTextMap[status as MrdStatus];
      setStatusText(statusText);
    }
  };

  const generateSampleType = (
    controlType: FlowcellControl | undefined,
    testType: TestTypes
  ) => {
    let sampleType: string = testType ?? '';
    switch (controlType) {
      case FlowcellControl.negative:
        sampleType = t('upload_negativeControl');
        break;
      case FlowcellControl.positive:
        sampleType = t('upload_positiveControl');
        break;
    }
    return sampleType;
  };

  return (
    /*
    This implementation was made because of CSS selector naming issue described in this w3 link:
    https://www.w3.org/TR/CSS2/syndata.html#characters

    The CSS class value set in this component was not allowing to have number
    digit(s) as starting value. For instance, the externalSubjectId had the following value: 1Subject123-1
    and it was being placed as CSS class value, in the root element which is a table-row element and when
    user clicks on this UI component a React Element issue was being shown and the app was unresponsive.
    To fix this issue, we just append a character, which is not a number or hyphen as advised by w3 link
    mentioned above, at the beginning of this value in order to avoid UI event error while clicking this
    component. For this case, we decided to set the letter c as a reliable fix, for each table row record
    class and when any record clicked no error will display anymore.
    */
    <tr
      className={`c${externalSubjectId} ${selected && 'selected'}`}
      onClick={select}
    >
      <td>
        <div className='subject-id'>
          <div className='well' style={{ background: displayColor }}></div>
          <div>{externalSubjectId}</div>
        </div>
      </td>
      <td>{`${name} (${sampleType})`}</td>
      <td>{wells.join(', ')}</td>
      {isNewExperiment ? (
        <td className={isHighlighted ? 'highlighted' : ''}>{t(statusText)}</td>
      ) : null}
    </tr>
  );
};

export default Subject;
